import React, { useState, useEffect, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, IconButton, Box, Tooltip, CircularProgress,
  Switch, Tabs, Tab, Button, Chip
} from '@mui/material';
import MainCard from 'components/MainCard';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Lottie from 'react-lottie';
import lottieLoader from '../../../assets/Lottie/NoLoaderData.json';

import { MdOutlineLockReset, MdEdit, MdEmail } from 'react-icons/md';
import { BsEyeFill } from 'react-icons/bs';
import toast from 'react-hot-toast';

import ResetPasswordDialog from 'components/coach/ResetPasswordDialog';
import CreateUserDialgue from './CreateUserDialgue';
import ViewManagerDialog from './ViewManagerDialog';
import EditManagerDialog from './EditManagerDialog';

import { getManager } from '../../../services/managerAndStaff/getManager';
import { updateStatus } from '../../../services/managerAndStaff/updateStatus';
import { resetUserPassword } from '../../../services/managerAndStaff/resetUserPassword';
import { sendCredential } from '../../../services/managerAndStaff/sendCredential';

const loaderOptions = {
  loop: true,
  autoplay: true,
  animationData: lottieLoader,
  rendererSettings: { preserveAspectRatio: 'xMidYMid slice' }
};

export default function ManagerList() {
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [sentCredentials, setSentCredentials] = useState(new Set()); // Track sent users

  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Fetch Managers
  const { data, isLoading, error } = useQuery({
    queryFn: getManager,
    queryKey: ['managers']
  });

  // Send Credentials Mutation
  const sendCredentialsMutation = useMutation({
    mutationFn: sendCredential,
    onSuccess: (response, variables) => {
      toast.success(response?.message || 'Login credentials sent successfully!');
      
      // Disable button for this user
      setSentCredentials(prev => new Set(prev).add(variables.userid));
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Failed to send credentials');
    }
  });

  // Enrich Manager Data
  const managers = useMemo(() => {
  if (!data) return [];

  return data.map((u) => ({
    userid: u.userid,
    name: u.authname || u.username || 'N/A',
    email: u.email || 'N/A',
    phone: u.phone || u.primarycontact || 'N/A',
    organization_name: u.organization_name || 'N/A',
    designation: u.designation || null,
    stateName: u.state_name || null,
    cityName: u.city_name || null,
    stateid: u.stateid || null,
    cityid: u.cityid || null,
    address: u.address || null,
    registrationDate: u.created
      ? new Date(u.created).toLocaleDateString()
      : 'N/A',

    status: u.status ?? 2,

    // ADD THIS
    credentials_sent: Number(u.credentials_sent) || 0
  }));
}, [data]);

  const allCount = managers.length;
  const activeCount = managers.filter((u) => u.status === 1).length;
  const inactiveCount = managers.filter((u) => u.status === 2).length;

  const filteredManagers = managers
    .filter((u) => {
      if (tabValue === 1) return u.status === 1;
      if (tabValue === 2) return u.status === 2;
      return true;
    })
    .filter((u) => u.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const toTitleCase = (str) => {
    return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const handleSendCredentials = (user) => {
    sendCredentialsMutation.mutate({
      userid: user.userid,
      name: user.name,
      email: user.email,
    });
  };

const isCredentialsSent = (userid) => {
  const user = managers.find((u) => u.userid === userid);

  return (
    sentCredentials.has(userid) ||
    Number(user?.credentials_sent) === 1
  );
};
  // Other handlers...
  const handleToggleStatus = (user) => {
    const newStatus = user.status === 1 ? 2 : 1;
    updateUserMutation.mutate({ userid: user.userid, status: newStatus });
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setOpenResetDialog(true);
  };

  const handleView = (user) => {
    setSelectedUser(user);
    setOpenViewDialog(true);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setOpenEditDialog(true);
  };

  // Mutations
  const updateUserMutation = useMutation({
    mutationFn: ({ userid, status }) => updateStatus(userid, status, 4),
    onSuccess: () => {
      queryClient.invalidateQueries(['managers']);
      toast.success('Status updated successfully');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Failed to update status'),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: resetUserPassword,
    onSuccess: () => {
      queryClient.invalidateQueries(['managers']);
      toast.success('Password reset successfully');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Failed to reset password'),
  });

  const confirmResetPassword = () => {
    if (!newPassword || !confirmPassword) return setPasswordError('Both fields are required.');
    if (newPassword !== confirmPassword) return setPasswordError('Passwords do not match.');

    resetPasswordMutation.mutate({ userid: selectedUser.userid, password: newPassword });
    setOpenResetDialog(false);
    setNewPassword(''); setConfirmPassword(''); setPasswordError('');
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>;
  }

  if (error) {
    return (
      <MainCard>
        <Box sx={{ p: 2, textAlign: 'center', color: 'error.main' }}>
          Error fetching managers: {error.message}
        </Box>
      </MainCard>
    );
  }

  return (
    <MainCard>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="All" icon={<Chip label={allCount} color="primary" size="small" />} iconPosition="end" />
          <Tab label="Active" icon={<Chip label={activeCount} color="success" size="small" />} iconPosition="end" />
          <Tab label="Inactive" icon={<Chip label={inactiveCount} color="error" size="small" />} iconPosition="end" />
        </Tabs>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField label="Search Manager" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          <Button variant="contained" color="primary" onClick={() => setOpenCreateDialog(true)}>
            Create Manager
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Joined Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredManagers.map((user, index) => (
              <TableRow key={user.userid}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{toTitleCase(user.name)}</TableCell>
                <TableCell>{user.organization_name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.phone}</TableCell>
                <TableCell>{user.registrationDate}</TableCell>
                <TableCell>
                  <Chip label={user.status === 1 ? 'Active' : 'Inactive'} color={user.status === 1 ? 'success' : 'error'} size="small" />
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Tooltip title="View">
                      <IconButton color="secondary" onClick={() => handleView(user)}>
                        <BsEyeFill />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton color="primary" onClick={() => handleEdit(user)}>
                        <MdEdit />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={isCredentialsSent(user.userid) ? "Credentials Already Sent" : "Send Login Credentials"}>
                      <IconButton
                        color="info"
                        onClick={() => handleSendCredentials(user)}
                        disabled={sendCredentialsMutation.isPending || isCredentialsSent(user.userid)}
                      >
                        {sendCredentialsMutation.isPending && sendCredentialsMutation.variables?.userid === user.userid 
                          ? <CircularProgress size={20} /> 
                          : <MdEmail />}
                      </IconButton>
                    </Tooltip>

                    <Switch
                      checked={user.status === 1}
                      onChange={() => handleToggleStatus(user)}
                      color="primary"
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialogs */}
      <CreateUserDialgue open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} onSuccess={() => queryClient.invalidateQueries(['managers'])} />
      <ViewManagerDialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} manager={selectedUser} />
      <EditManagerDialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} manager={selectedUser} onSuccess={() => queryClient.invalidateQueries(['managers'])} />
      <ResetPasswordDialog open={openResetDialog} onClose={() => setOpenResetDialog(false)} coach={selectedUser} /* ... other props */ />
    </MainCard>
  );
}